import React, { useMemo } from "react";
import {
  HrDepartmentMapping,
  useHrDepartmentsManager,
} from "@src/pages/headcount-tracking/mapping/useHrDepartmentsManager";
import { Button, Count, Select } from "@src/components/elements";
import { TableSkeleton } from "@src/components/elements/Table/TableSkeleton";
import { Checkbox } from "@src/components/elements/Checkbox";
import { Cell, Column } from "react-table";
import { Table } from "@src/components/modules/Table";
import { matchNameToObject } from "@src/utils/matchNameToObject";
import styled from "styled-components/macro";

export const HrMappingStyleDiv = styled.div`
  margin: 2rem;

  h2 {
    margin-bottom: 1rem;
  }

  .mapping-section {
    margin-bottom: 2rem;
  }
`;

export const HrDepartmentsManager = ({
  onSave,
  shouldSplitByUnmapped,
}: {
  onSave: () => void;
  shouldSplitByUnmapped: boolean;
}) => {
  const {
    hrDepartments,
    refetch,
    updateHrDepartment,
    saveHrDepartments,
    onNewDepartmentName,
    onDepartmentChange,
    loading,
    departmentOptions,
    departments,
  } = useHrDepartmentsManager();

  const columns = useMemo<Column<HrDepartmentMapping>[]>(
    () => [
      {
        Header: "HR Department",
        accessor: "name",
        disableSortBy: true,
        Cell: function Cell({ row }: Cell<HrDepartmentMapping>) {
          return <div className="row">{row.original.name}</div>;
        },
      },
      {
        Header: "Map in Sudozi",
        id: "map-in-sudozi",
        disableSortBy: true,
        Cell: function mapInSudoziCell({ row }: Cell<HrDepartmentMapping>) {
          const { id, departmentId, name, newDepartmentName } = row.original;

          return (
            <div className="row">
              <Checkbox
                onClick={() => {
                  if (!departmentId && !newDepartmentName) {
                    const nameMatchedDepartmentId = matchNameToObject(
                      name,
                      departments,
                    );
                    let newDepartmentName: string | null = null;

                    if (!nameMatchedDepartmentId) {
                      newDepartmentName = name;
                    }

                    updateHrDepartment(
                      id,
                      nameMatchedDepartmentId,
                      newDepartmentName,
                    );
                  } else {
                    updateHrDepartment(id, null, null);
                  }
                }}
                value={!!departmentId || !!newDepartmentName}
                label=""
                canShowError={false}
              />
            </div>
          );
        },
      },
      {
        Header: "Sudozi Department",
        accessor: "departmentId",
        disableSortBy: true,
        Cell: function Cell({ row }: Cell<HrDepartmentMapping>) {
          const { departmentId, newDepartmentName } = row.original;

          const departmentOption = departmentId
            ? departmentOptions.find((option) => option.value === departmentId)
            : {
                label: `Create "${newDepartmentName}"`,
                value: newDepartmentName ?? "",
              };

          const cell = (
            <div>
              <Select
                isDisabled={!departmentId && !newDepartmentName}
                options={departmentOptions}
                value={departmentOption ?? null}
                menuPortalTarget={document.querySelector("body")}
                onChange={(value) => onDepartmentChange(value, row.original.id)}
                isCreatable
                onCreateOption={(newDepartmentName) =>
                  onNewDepartmentName(newDepartmentName, row.original.id)
                }
                isClearable={false}
                disableError={true}
              />
            </div>
          );
          return cell;
        },
      },
    ],
    [
      departmentOptions,
      departments,
      onDepartmentChange,
      onNewDepartmentName,
      updateHrDepartment,
    ],
  );

  return loading ? (
    <TableSkeleton numColumns={20} numRows={5} />
  ) : (
    <HrMappingStyleDiv>
      {shouldSplitByUnmapped ? (
        <>
          <div className="mapping-section">
            <h2>Unmapped HR departments</h2>
            <Table
              columns={columns}
              data={hrDepartments.filter((d) => d.isInitiallyUnmapped)}
            />
          </div>

          <div className="mapping-section">
            <h2>Mapped HR departments</h2>
            <Table
              columns={columns}
              data={hrDepartments.filter((d) => !d.isInitiallyUnmapped)}
            />
          </div>
        </>
      ) : (
        <>
          <h2>
            HR Departments <Count color="gray" count={hrDepartments.length} />
          </h2>

          <Table columns={columns} data={hrDepartments} />
        </>
      )}

      <div className="action-buttons">
        <Button
          onClick={async () => {
            await saveHrDepartments({
              variables: {
                hrDepartments: hrDepartments.map((d) => ({
                  id: d.id,
                  departmentId: d.departmentId,
                  newDepartmentName: d.newDepartmentName,
                })),
              },
            });
            refetch();
            onSave();
          }}
        >
          Save
        </Button>
      </div>
    </HrMappingStyleDiv>
  );
};
